import { useState } from "react";
import { Button, Message, useToast } from "@humansignal/ui";
import { useAPI } from "@humansignal/core";
import { Modal } from "../../components/Modal/Modal";
import { useTranslation } from "../../i18n/LocaleProvider";

/**
 * RequestLanguageModal — lets any signed-in user ask for the app to be
 * translated into a language that isn't offered yet. It records a
 * ``LanguageRequest`` that platform staff can one-click turn into a
 * translation project (see the Platform → Localization queue).
 */
export const RequestLanguageModal = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();
  const api = useAPI();
  const toast = useToast();

  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const language = name.trim();
    if (!language) {
      setError(
        t("pages.discover.request_language_required", undefined, "Tell us which language you want to translate."),
      );
      return;
    }
    setBusy(true);
    setError(null);
    const res = await api.callApi("createLanguageRequest", {
      body: { language_name: language, language_code: code.trim(), note: note.trim() },
      suppressError: true,
    });
    setBusy(false);
    if (res?.id) {
      toast.show({
        message: t(
          "pages.discover.request_language_sent",
          { language },
          `Thanks, we've noted your request to translate into ${language}.`,
        ),
      });
      onClose();
      return;
    }
    const detail = (res as any)?.response?.detail || (res as any)?.error;
    setError(
      typeof detail === "string"
        ? detail
        : t("pages.discover.request_language_failed", undefined, "Couldn't send your request. Please try again."),
    );
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    height: 38,
    padding: "0 12px",
    borderRadius: 8,
    border: "1px solid var(--color-neutral-border)",
    background: "var(--color-neutral-background)",
    color: "var(--color-neutral-content)",
    fontSize: "var(--font-size-body-medium)",
  };
  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: "var(--font-size-label-small)",
    fontWeight: "var(--font-weight-medium)" as any,
    color: "var(--color-neutral-content-subtle)",
    margin: "0 0 4px",
  };

  return (
    <Modal
      visible
      onHide={onClose}
      title={t("pages.discover.request_language_title", undefined, "Request a language")}
      style={{ width: 460 }}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button variant="neutral" look="outlined" size="small" onClick={onClose} disabled={busy}>
            {t("common.cancel", undefined, "Cancel")}
          </Button>
          <Button variant="primary" size="small" onClick={submit} disabled={busy || !name.trim()} waiting={busy}>
            {t("pages.discover.request_language_submit", undefined, "Send request")}
          </Button>
        </div>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "4px 2px" }}>
        <p
          style={{
            margin: 0,
            fontSize: "var(--font-size-body-small)",
            color: "var(--color-neutral-content-subtle)",
            lineHeight: 1.5,
          }}
        >
          {t(
            "pages.discover.request_language_intro",
            undefined,
            "Don't see your language? Tell us and we'll open a project you can help translate.",
          )}
        </p>

        {error && (
          <Message variant="negative" size="small">
            {error}
          </Message>
        )}

        <div>
          <label style={labelStyle}>{t("pages.discover.request_language_name", undefined, "Language")}</label>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("pages.discover.request_language_name_ph", undefined, "e.g. Fulfulde")}
            style={inputStyle}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
            }}
          />
        </div>

        <div>
          <label style={labelStyle}>{t("pages.discover.request_language_code", undefined, "Language code")}</label>
          <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. ff" style={inputStyle} />
        </div>

        <div>
          <label style={labelStyle}>{t("pages.discover.request_language_note", undefined, "Anything to add?")}</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder={t(
              "pages.discover.request_language_note_ph",
              undefined,
              "I'm a native speaker and happy to translate.",
            )}
            style={{ ...inputStyle, height: "auto", padding: "8px 12px", resize: "vertical" }}
          />
        </div>
      </div>
    </Modal>
  );
};

export default RequestLanguageModal;
